// frontend/src/pages/shop/Deliveries.jsx
import { useState, useEffect } from 'react';
import ShopLayout from '../../components/layout/ShopLayout';
import { shopAdminAPI } from '../../services/api';
import CreateDeliveryModal from '../../components/shop/CreateDeliveryModal';

const Deliveries = () => {
  const [deliveries, setDeliveries] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedDelivery, setSelectedDelivery] = useState(null);

  // Pagination
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });
  
  // Filters
  const [dateRange, setDateRange] = useState('today');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Format helpers
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('mn-MN').format(amount || 0) + '₮';
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('mn-MN');
  };

  const formatDateTime = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleString('mn-MN');
  };

  const getDateRangeLabel = () => {
    switch (dateRange) {
      case 'today': return 'Өнөөдөр';
      case 'yesterday': return 'Өчигдөр';
      case 'week': return 'Сүүлийн 7 хоног';
      case 'month': return 'Сүүлийн 30 хоног';
      case 'custom': return 'Сонгосон хугацаа';
      default: return 'Өнөөдөр';
    }
  };

   useEffect(() => {
    fetchDeliveries();
  }, [pagination.page]);

  const fetchDeliveries = async () => {
    try {
      setLoading(true);
      setError(null);
      
       const params = {
        status: statusFilter,
        date_range: dateRange,
        page: pagination.page,
        limit: pagination.limit
      };

      if (dateRange === 'custom' && customStartDate && customEndDate) {
        params.start_date = customStartDate;
        params.end_date = customEndDate;
      }
      
      const response = await shopAdminAPI.getDeliveries(params);
      
       if (response.data.success) {
        // Давхардал арилгах - ID-аар unique болгох
        const uniqueDeliveries = (response.data.data || []).filter((item, index, self) =>
          index === self.findIndex((t) => t.id === item.id)
        );
        setDeliveries(uniqueDeliveries);
        setStats(response.data.stats || null);
        // Pagination мэдээлэл
        if (response.data.pagination) {
          setPagination(prev => ({
            ...prev,
            total: response.data.pagination.total || 0,
            totalPages: response.data.pagination.pages || 0
          }));
        }
      } else {
        setError(response.data.message || 'Хүргэлт татахад алдаа гарлаа');
      }
    } catch (err) {
      console.error('Fetch deliveries error:', err);
      setError(err.response?.data?.message || 'Хүргэлт татахад алдаа гарлаа');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetail = async (delivery) => {
    try {
      setExpandedItems(false);
      const response = await shopAdminAPI.getDeliveryById(delivery.id);
      setSelectedDelivery(response.data.data);
      setShowDetailModal(true);
    } catch (error) {
      console.error('Detail error:', error);
      setSelectedDelivery(delivery);
      setShowDetailModal(true);
    }
  };

   const [expandedItems, setExpandedItems] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editDelivery, setEditDelivery] = useState(null);
  const [editItems, setEditItems] = useState([]);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [editProductSearch, setEditProductSearch] = useState('');

  const handleEdit = async (delivery) => {
    try {
      // Хүргэлтийн дэлгэрэнгүй (бараатай) татах
      const response = await shopAdminAPI.getDeliveryById(delivery.id);
      const detail = response.data.data;
      
      setEditDelivery({
        id: detail.id,
        customer_name: detail.customer_name || '',
        customer_phone: detail.customer_phone || '',
        address: detail.address || '',
        cod_amount: detail.cod_amount || 0,
        payment_status: detail.payment_status || 'unpaid',
        notes: detail.notes || ''
      });

      // Одоогийн бараанууд
      const currentItems = detail.outbound?.items?.map(item => ({
        product_id: item.product_id,
        variant_id: item.variant_id || null,
        quantity: item.quantity,
        unit_price: item.unit_price || 0,
        product_name: item.product?.product_name || 'Бараа',
        variant_name: item.variant?.variant_name || null,
        sku: item.product?.sku || item.variant?.sku || ''
      })) || [];
      setEditItems(currentItems);

      // Бүх бараа татах
      const prodResponse = await shopAdminAPI.getProducts({ limit: 500 });
      setAvailableProducts(prodResponse.data.data?.products || prodResponse.data.data || []);
      
      setShowEditModal(true);
    } catch (error) {
      console.error('Edit load error:', error);
      alert('Хүргэлтийн мэдээлэл татахад алдаа гарлаа');
    }
  };

   const calculateCodFromItems = (items, paymentStatus) => {
    if (paymentStatus === 'prepaid') return 0;
    return items.reduce((sum, item) => sum + (parseFloat(item.unit_price) * item.quantity), 0);
  };

  const handleEditItemQuantity = (index, newQty) => {
    if (newQty < 1) return;
    const newItems = editItems.map((item, i) => i === index ? { ...item, quantity: newQty } : item);
    setEditItems(newItems);
    setEditDelivery(prev => ({ ...prev, cod_amount: calculateCodFromItems(newItems, prev.payment_status) }));
  };

  const handleEditRemoveItem = (index) => {
    const newItems = editItems.filter((_, i) => i !== index);
    setEditItems(newItems);
    setEditDelivery(prev => ({ ...prev, cod_amount: calculateCodFromItems(newItems, prev.payment_status) }));
  };

  const handleEditAddProduct = (product) => {
    // Бараа аль хэдийн нэмэгдсэн эсэх
    const exists = editItems.find(item => item.product_id === product.id && !item.variant_id);
    if (exists) {
      alert('Энэ бараа аль хэдийн нэмэгдсэн байна');
      return;
    }
    const newItem = {
      product_id: product.id,
      variant_id: null,
      quantity: 1,
      unit_price: product.base_price || 0,
      product_name: product.product_name,
      variant_name: null,
      sku: product.sku || ''
    };
    const newItems = [...editItems, newItem];
    setEditItems(newItems);
    setEditDelivery(prev => ({ ...prev, cod_amount: calculateCodFromItems(newItems, prev.payment_status) }));
    setEditProductSearch('');
  };

  const handleEditSave = async () => {
    if (editItems.length === 0) {
      alert('Бараа нэмнэ үү');
      return;
    }
    try {
      const data = {
        ...editDelivery,
        items: editItems.map(item => ({
          product_id: item.product_id,
          variant_id: item.variant_id,
          quantity: item.quantity,
          unit_price: item.unit_price
        }))
      };
      const response = await shopAdminAPI.updateDelivery(editDelivery.id, data);
      if (response.data.success) {
        alert('Хүргэлт амжилттай засагдлаа');
        setShowEditModal(false);
        setEditDelivery(null);
        setEditItems([]);
        fetchDeliveries();
      }
    } catch (error) {
      console.error('Edit error:', error);
      alert(error.response?.data?.message || 'Хүргэлт засахад алдаа гарлаа');
    }
  };



  const handleDelete = async (id) => {
    if (!confirm('Энэ хүргэлтийг устгахдаа итгэлтэй байна уу?\n\nБарааны үлдэгдэл буцаагдана.')) return;
    
    try {
      const response = await shopAdminAPI.deleteDelivery(id);
      if (response.data.success) {
        alert('Хүргэлт амжилттай устгагдлаа');
        fetchDeliveries();
      } else {
        alert(response.data.message || 'Устгахад алдаа гарлаа');
      }
    } catch (error) {
      console.error('Delete error:', error);
      alert(error.response?.data?.message || 'Хүргэлт устгахад алдаа гарлаа');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { label: 'Хүлээгдэж буй', color: 'bg-yellow-100 text-yellow-800' },
      in_transit: { label: 'Хүргэж байгаа', color: 'bg-blue-100 text-blue-800' },
      delivered: { label: 'Хүргэсэн', color: 'bg-green-100 text-green-800' },
      cancelled: { label: 'Цуцалсан', color: 'bg-red-100 text-red-800' },
    };
    const badge = badges[status] || badges.pending;
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${badge.color}`}>
        {badge.label}
      </span>
    );
  };

  const getPaymentBadge = (status) => {
    const badges = {
      unpaid: { label: 'Төлөгдөөгүй', color: 'bg-red-100 text-red-800' },
      paid_by_driver: { label: 'Жолооч авсан', color: 'bg-blue-100 text-blue-800' },
      paid_by_company: { label: 'Компанид шилжсэн', color: 'bg-green-100 text-green-800' },
      prepaid: { label: 'Урьдчилсан', color: 'bg-purple-100 text-purple-800' },
    };
    const badge = badges[status] || badges.unpaid;
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${badge.color}`}>
        {badge.label}
      </span>
    );
  };

  const canDelete = (delivery) => {
    return delivery.status === 'pending' && !delivery.assigned_driver_id;
  };

  // Calculate stats
  const calculatedStats = {
    total: stats?.total || deliveries.length,
    delivered: stats?.delivered || deliveries.filter(d => d.status === 'delivered').length,
    pending: stats?.pending || deliveries.filter(d => d.status === 'pending').length,
    in_transit: stats?.in_transit || deliveries.filter(d => d.status === 'in_transit').length,
    cancelled: stats?.cancelled || deliveries.filter(d => d.status === 'cancelled').length,
    total_cod: stats?.total_amount || deliveries.reduce((sum, d) => sum + parseFloat(d.cod_amount || 0), 0),
    paid_amount: stats?.paid_amount || 0,
  };
  calculatedStats.unpaid_amount = calculatedStats.total_cod - calculatedStats.paid_amount;

  if (loading) {
    return (
      <ShopLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-500">Уншиж байна...</p>
          </div>
        </div>
      </ShopLayout>
    );
  }

  if (error) {
    return (
      <ShopLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <span className="text-5xl">❌</span>
            <p className="mt-4 text-red-600 font-medium">{error}</p>
            <button 
              onClick={fetchDeliveries}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Дахин оролдох
            </button>
          </div>
        </div>
      </ShopLayout>
    );
  }

  return (
    <ShopLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Хүргэлт</h1>
            <p className="text-gray-500 mt-1">Хүргэлтийн удирдлага</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="w-full sm:w-auto px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2 font-medium"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span>Хүргэлт шивэх</span>
          </button>
        </div>

        {/* Filter */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* Date Range Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Хугацаа</label>
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              >
                <option value="today">Өнөөдөр</option>
                <option value="yesterday">Өчигдөр</option>
                <option value="week">Сүүлийн 7 хоног</option>
                <option value="month">Сүүлийн 30 хоног</option>
                <option value="custom">Хугацаа сонгох</option>
              </select>
            </div>

            {dateRange === 'custom' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Эхлэх огноо</label>
                  <input
                    type="date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Дуусах огноо</label>
                  <input
                    type="date"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                </div>
              </>
            )}

            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Төлөв</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              >
                <option value="all">Бүгд</option>
                <option value="pending">Хүлээгдэж буй</option>
                <option value="in_transit">Хүргэж байгаа</option>
                <option value="delivered">Хүргэсэн</option>
                <option value="cancelled">Цуцалсан</option>
              </select>
            </div>

            <div className={`flex items-end ${dateRange === 'custom' ? '' : 'sm:col-span-1 lg:col-span-2'}`}>
              <button
                  onClick={() => {
                    setPagination(prev => ({ ...prev, page: 1 }));
                    fetchDeliveries();
                  }}
                  disabled={loading}
                  className="w-full sm:w-auto px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700
disabled:opacity-50 flex items-center justify-center gap-2 text-sm font-medium transition-colors"
              >
                {loading ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                )}
                <span>Хайх</span>
              </button>
            </div>
          </div>

          {/* Active Filter Info */}
          <div className="mt-3 pt-3 border-t border-gray-100">
            <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600">
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded-md">
                <span>📅</span>
                <span className="font-medium">{getDateRangeLabel()}</span>
              </span>
              {statusFilter !== 'all' && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-50 text-purple-700 rounded-md">
                  <span>🏷️</span>
                  <span className="font-medium">
                    {statusFilter === 'pending' ? 'Хүлээгдэж буй' : 
                     statusFilter === 'delivered' ? 'Хүргэсэн' :
                     statusFilter === 'in_transit' ? 'Хүргэж байгаа' :
                     statusFilter === 'cancelled' ? 'Цуцалсан' : statusFilter}
                  </span>
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Summary Cards - Same as MySettlement */}
        {/* Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {/* Card 1: Нийт хүргэлт */}
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl sm:rounded-2xl p-3 sm:p-5 text-white shadow-lg">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <p className="text-blue-100 text-xs sm:text-sm font-medium">Нийт хүргэлт</p>
                <p className="text-xl sm:text-3xl font-bold mt-1 sm:mt-2">{calculatedStats.total || 0}</p>
                <p className="text-blue-100 text-xs sm:text-sm mt-1 sm:mt-2 truncate">{getDateRangeLabel()}</p>
              </div>
              <div className="w-8 h-8 sm:w-12 sm:h-12 bg-white/20 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0">
                <span className="text-base sm:text-2xl">📦</span>
              </div>
            </div>
          </div>

          {/* Card 2: Хүргэсэн */}
          <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl sm:rounded-2xl p-3 sm:p-5 text-white shadow-lg">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <p className="text-emerald-100 text-xs sm:text-sm font-medium">Хүргэсэн</p>
                <p className="text-xl sm:text-3xl font-bold mt-1 sm:mt-2">{calculatedStats.delivered || 0}</p>
                <div className="mt-1 sm:mt-2">
                  <div className="w-full bg-white/30 rounded-full h-1 sm:h-1.5">
                    <div 
                      className="bg-white h-1 sm:h-1.5 rounded-full" 
                      style={{ width: `${calculatedStats.total > 0 ? Math.round((calculatedStats.delivered / calculatedStats.total) * 100) : 0}%` }}
                    ></div>
                  </div>
                  <p className="text-emerald-100 text-xs sm:text-sm mt-1 truncate">
                    {calculatedStats.total > 0 ? Math.round((calculatedStats.delivered / calculatedStats.total) * 100) : 0}%
                  </p>
                </div>
              </div>
              <div className="w-8 h-8 sm:w-12 sm:h-12 bg-white/20 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0">
                <span className="text-base sm:text-2xl">✅</span>
              </div>
            </div>
          </div>

          {/* Card 3: Хүлээгдэж буй */}
          <div className="bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl sm:rounded-2xl p-3 sm:p-5 text-white shadow-lg">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <p className="text-amber-100 text-xs sm:text-sm font-medium">Хүлээгдэж буй</p>
                <p className="text-xl sm:text-3xl font-bold mt-1 sm:mt-2">{(calculatedStats.pending || 0) + (calculatedStats.in_transit || 0)}</p>
                <div className="flex items-center gap-2 sm:gap-3 mt-1 sm:mt-2">
                  <span className="text-white text-xs sm:text-sm">⏳{calculatedStats.pending || 0}</span>
                  <span className="text-red-100 text-xs sm:text-sm">❌{calculatedStats.cancelled || 0}</span>
                </div>
              </div>
              <div className="w-8 h-8 sm:w-12 sm:h-12 bg-white/20 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0">
                <span className="text-base sm:text-2xl">⏰</span>
              </div>
            </div>
          </div>

          {/* Card 4: Төлбөрийн дүн */}
          <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl sm:rounded-2xl p-3 sm:p-5 text-white shadow-lg">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <p className="text-purple-100 text-xs sm:text-sm font-medium">Нийт дүн</p>
                <p className="text-lg sm:text-2xl font-bold mt-1 sm:mt-2 truncate">{formatCurrency(calculatedStats.total_cod)}</p>
                <div className="flex flex-col mt-1 sm:mt-2">
                  <span className="text-green-200 text-xs sm:text-sm truncate">✅{formatCurrency(calculatedStats.paid_amount)}</span>
                  <span className="text-red-200 text-xs sm:text-sm truncate">⏳{formatCurrency(calculatedStats.unpaid_amount)}</span>
                </div>
              </div>
              <div className="w-8 h-8 sm:w-12 sm:h-12 bg-white/20 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0">
                <span className="text-base sm:text-2xl">💰</span>
              </div>
            </div>
          </div>
        </div>

        {/* Deliveries Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-gray-50 to-white">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <span>📋</span> Хүргэлтийн жагсаалт
            </h3>
            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
              {deliveries.length} хүргэлт
            </span>
          </div>
          
          {deliveries.length === 0 ? (
            <div className="p-12 text-center">
              <span className="text-5xl">📭</span>
              <h3 className="text-xl font-bold text-gray-900 mt-4">Хүргэлт олдсонгүй</h3>
              <p className="text-gray-500 mt-2">Эхний хүргэлтээ шивэх үү?</p>
              <button
                onClick={() => setShowCreateModal(true)}
                className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium"
              >
                Хүргэлт шивэх
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto -mx-4 sm:mx-0">
              <div className="max-h-[400px] overflow-y-auto px-4 sm:px-0">
                <table className="w-full" style={{ minWidth: '600px' }}>
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">№</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Дугаар</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Хүлээн авагч</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Хаяг</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Дүн</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Төлөв</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Төлбөр</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Үйлдэл</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {deliveries.map((delivery, index) => (
                      <tr key={delivery.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3 text-sm text-gray-500">{index + 1}</td>
                        <td className="px-4 py-3">
                          <span className="text-sm font-medium text-blue-600">{delivery.delivery_number || delivery.order_number}</span>
                        </td>
                        <td className="px-4 py-3">
                          <div>
                            <p className="text-sm font-medium text-gray-900">{delivery.customer_name}</p>
                            <p className="text-xs text-gray-500">{delivery.customer_phone}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-sm text-gray-600 max-w-[200px] truncate">{delivery.address}</p>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className="text-sm font-semibold text-gray-900">{formatCurrency(delivery.cod_amount)}</span>
                        </td>
                        <td className="px-4 py-3 text-center">{getStatusBadge(delivery.status)}</td>
                        <td className="px-4 py-3 text-center">{getPaymentBadge(delivery.payment_status)}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-center gap-1">
                            <button
                              onClick={() => handleViewDetail(delivery)}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Дэлгэрэнгүй"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                            </button>
                            {canDelete(delivery) && (
                                <>
                                  <button
                                    onClick={() => handleEdit(delivery)}
                                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                    title="Засах"
                                  >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                    </svg>
                                  </button>
                                  <button
                                    onClick={() => handleDelete(delivery.id)}
                                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                    title="Устгах"
                                  >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                  </button>
                                </>
                              )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* CREATE DELIVERY MODAL */}
      {showCreateModal && (
        <CreateDeliveryModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            fetchDeliveries();
          }}
        />
      )}

      {/* DETAIL MODAL */}
      {showDetailModal && selectedDelivery && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[95vh] overflow-hidden">
            {/* Header */}
            <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-blue-500 to-blue-600">
              <div className="flex items-center justify-between">
                <div className="text-white">
                  <h2 className="text-xl font-bold">{selectedDelivery.delivery_number || selectedDelivery.order_number}</h2>
                  <p className="text-blue-100 text-sm">{formatDateTime(selectedDelivery.created_at)}</p>
                </div>
                <button 
                  onClick={() => setShowDetailModal(false)} 
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors text-white"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(95vh-180px)]">
              <div className="space-y-6">
                {/* Status Badges */}
                <div className="flex flex-wrap gap-3">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500">Төлөв:</span>
                    {getStatusBadge(selectedDelivery.status)}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500">Төлбөр:</span>
                    {getPaymentBadge(selectedDelivery.payment_status)}
                  </div>
                </div>

                {/* Customer Info */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <span>👤</span> Хүлээн авагч
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-gray-500">Нэр</p>
                      <p className="text-sm font-medium text-gray-900">{selectedDelivery.customer_name}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Утас</p>
                      <p className="text-sm font-medium text-gray-900">{selectedDelivery.customer_phone}</p>
                    </div>
                    <div className="sm:col-span-2">
                      <p className="text-xs text-gray-500">Хаяг</p>
                      <p className="text-sm font-medium text-gray-900">{selectedDelivery.address}</p>
                    </div>
                  </div>
                </div>

                {/* Payment Info */}
                <div className="bg-purple-50 rounded-xl p-4 border border-purple-100">
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <span>💰</span> Төлбөр
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-gray-500">Авах дүн (COD)</p>
                      <p className="text-xl font-bold text-purple-700">{formatCurrency(selectedDelivery.cod_amount)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Хүргэлтийн төлбөр</p>
                      <p className="text-xl font-bold text-gray-900">{formatCurrency(selectedDelivery.delivery_fee)}</p>
                    </div>
                  </div>
                </div>

                {/* EXPANDABLE ITEMS */}
                {selectedDelivery.outbound?.items && selectedDelivery.outbound.items.length > 0 && (
                  <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                    {!expandedItems ? (
                      <>
                        <div className="flex items-center justify-between">
                          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                            <span>📦</span> Бараанууд
                            <span className="text-xs bg-blue-200 text-blue-800 px-2 py-0.5 rounded-full">
                              {selectedDelivery.outbound.items.length} төрөл
                            </span>
                          </h3>
                          <button
                            onClick={() => setExpandedItems(true)}
                            className="text-xs text-blue-600 font-semibold hover:text-blue-700 flex items-center gap-1"
                          >
                            Дэлгэрэнгүй ▼
                          </button>
                        </div>
                        <div className="mt-2 space-y-1">
                          {selectedDelivery.outbound.items.slice(0, 3).map((item, idx) => (
                            <div key={idx} className="flex items-center justify-between text-sm">
                              <span className="text-gray-700">
                                • {item.product?.product_name} <span className="text-gray-400">×{item.quantity}</span>
                              </span>
                              <span className="text-gray-600 font-medium">
                                {(Number(item.unit_price || 0) * item.quantity).toLocaleString()}₮
                              </span>
                            </div>
                          ))}
                          {selectedDelivery.outbound.items.length > 3 && (
                            <p className="text-xs text-gray-500 pl-3">... +{selectedDelivery.outbound.items.length - 3} бараа</p>
                          )}
                        </div>
                        <div className="mt-2 pt-2 border-t border-blue-200 flex justify-between items-center">
                          <span className="text-sm text-gray-600">Нийт дүн:</span>
                          <span className="text-base font-bold text-blue-700">
                            {selectedDelivery.outbound.items.reduce((sum, item) => sum + (Number(item.unit_price || 0) * item.quantity), 0).toLocaleString()}₮
                          </span>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                            <span>📦</span> Бараанууд
                            <span className="text-xs bg-blue-200 text-blue-800 px-2 py-0.5 rounded-full">
                              {selectedDelivery.outbound.items.length} төрөл
                            </span>
                          </h3>
                          <button
                            onClick={() => setExpandedItems(false)}
                            className="text-xs text-blue-600 font-semibold hover:text-blue-700 flex items-center gap-1"
                          >
                            Хураах ▲
                          </button>
                        </div>
                        <div className="overflow-x-auto bg-white rounded-lg border border-blue-200">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="bg-blue-100/50">
                                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 w-8">№</th>
                                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Бараа</th>
                                <th className="px-3 py-2 text-center text-xs font-semibold text-gray-600 w-12">Тоо</th>
                                <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600 w-24">Нэгж үнэ</th>
                                <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600 w-24">Дүн</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {selectedDelivery.outbound.items.map((item, index) => (
                                <tr key={index}>
                                  <td className="px-3 py-2 text-gray-400 text-xs">{index + 1}</td>
                                  <td className="px-3 py-2">
                                    <p className="font-medium text-gray-900 text-sm">{item.product?.product_name || '-'}</p>
                                    {item.variant?.variant_name && (
                                      <p className="text-xs text-gray-500">{item.variant.variant_name}</p>
                                    )}
                                    <p className="text-xs text-gray-400">{item.product?.sku || ''}</p>
                                  </td>
                                  <td className="px-3 py-2 text-center font-medium text-gray-900">{item.quantity}</td>
                                  <td className="px-3 py-2 text-right text-gray-600">{Number(item.unit_price || 0).toLocaleString()}₮</td>
                                  <td className="px-3 py-2 text-right font-bold text-blue-600">{(Number(item.unit_price || 0) * item.quantity).toLocaleString()}₮</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        <div className="mt-3 pt-2 border-t border-blue-200 flex justify-between items-center">
                          <span className="text-sm font-medium text-gray-600">Нийт дүн:</span>
                          <span className="text-lg font-bold text-blue-700">
                            {selectedDelivery.outbound.items.reduce((sum, item) => sum + (Number(item.unit_price || 0) * item.quantity), 0).toLocaleString()}₮
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* Delivery Info */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <span>🚚</span> Хүргэлт
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-gray-500">Хүргэх огноо</p>
                      <p className="text-sm font-medium text-gray-900">{formatDate(selectedDelivery.delivery_date)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Жолооч</p>
                      <p className="text-sm font-medium text-gray-900">
                        {selectedDelivery.driver?.driver_name || 
                         (selectedDelivery.assigned_driver_id ? 'Хувиарлагдсан' : 'Хувиарлагдаагүй')}
                      </p>
                    </div>
                    {selectedDelivery.driver?.phone && (
                      <div>
                        <p className="text-xs text-gray-500">Жолоочийн утас</p>
                        <p className="text-sm font-medium text-gray-900">{selectedDelivery.driver.phone}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Timeline */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <span>📋</span> Түүх
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                        <span className="text-sm">✅</span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">Үүсгэсэн</p>
                        <p className="text-xs text-gray-500">{formatDateTime(selectedDelivery.created_at)}</p>
                      </div>
                    </div>
                    {selectedDelivery.assigned_at && (
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                          <span className="text-sm">👨‍✈️</span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">Жолоочид хувиарлагдсан</p>
                          <p className="text-xs text-gray-500">{formatDateTime(selectedDelivery.assigned_at)}</p>
                        </div>
                      </div>
                    )}
                    {selectedDelivery.completed_at && (
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                          <span className="text-sm">🎉</span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">Хүргэгдсэн</p>
                          <p className="text-xs text-gray-500">{formatDateTime(selectedDelivery.completed_at)}</p>
                        </div>
                      </div>
                    )}
                    {selectedDelivery.cancelled_at && (
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                          <span className="text-sm">❌</span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">Цуцлагдсан</p>
                          <p className="text-xs text-gray-500">{formatDateTime(selectedDelivery.cancelled_at)}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Delivery Instructions */}
                {selectedDelivery.delivery_instructions && (
                  <div className="bg-yellow-50 rounded-xl p-4 border border-yellow-200">
                    <h3 className="font-semibold text-yellow-800 mb-2 flex items-center gap-2">
                      <span>📝</span> Хүргэлтийн заавар
                    </h3>
                    <p className="text-yellow-700">{selectedDelivery.delivery_instructions}</p>
                  </div>
                )}

                {/* Notes */}
                {selectedDelivery.notes && (
                  <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                    <h3 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
                      <span>🏪</span> Онлайн шопын тайлбар
                    </h3>
                    <p className="text-blue-700">{selectedDelivery.notes}</p>
                  </div>
                )}

                {/* Postpone/Cancel Reason */}
                {(selectedDelivery.postpone_reason || selectedDelivery.cancel_reason) && (
                  <div className={`rounded-xl p-3 border ${
                    selectedDelivery.status === 'cancelled' 
                      ? 'bg-red-50 border-red-200' 
                      : 'bg-orange-50 border-orange-200'
                  }`}>
                    <h3 className={`text-sm font-medium mb-1 flex items-center gap-2 ${
                      selectedDelivery.status === 'cancelled' ? 'text-red-700' : 'text-orange-700'
                    }`}>
                      <span className="text-sm">{selectedDelivery.status === 'cancelled' ? '❌' : '⏸️'}</span>
                      {selectedDelivery.status === 'cancelled' ? 'Цуцалсан шалтгаан' : 'Хойшлуулсан шалтгаан'}
                      <span className="text-xs font-normal text-gray-500">(Жолоочийн тайлбар)</span>
                    </h3>
                    <p className={`text-sm ${
                      selectedDelivery.status === 'cancelled' ? 'text-red-600' : 'text-orange-600'
                    }`}>
                      {selectedDelivery.postpone_reason === 'customer_unavailable' && 'Хүлээн авагч байхгүй'}
                      {selectedDelivery.postpone_reason === 'phone_unreachable' && 'Утас авахгүй'}
                      {selectedDelivery.postpone_reason === 'address_not_found' && 'Хаяг олдохгүй'}
                      {selectedDelivery.postpone_reason === 'reschedule_request' && 'Өөр цагт хүргэх хүсэлт'}
                      {selectedDelivery.cancel_reason === 'customer_refused' && 'Хүлээн авагч татгалзсан'}
                      {selectedDelivery.cancel_reason === 'wrong_address' && 'Хаяг буруу'}
                      {selectedDelivery.cancel_reason === 'damaged_goods' && 'Бараа гэмтсэн'}
                      {selectedDelivery.cancel_reason === 'order_cancelled' && 'Захиалга цуцлагдсан'}
                      {(selectedDelivery.postpone_reason === 'other' || selectedDelivery.cancel_reason === 'other') && 'Бусад'}
                    </p>
                    {(selectedDelivery.postpone_reason_text || selectedDelivery.cancel_reason_text) && (
                      <p className={`mt-1 text-xs ${
                        selectedDelivery.status === 'cancelled' ? 'text-red-500' : 'text-orange-500'
                      }`}>
                        💬 {selectedDelivery.postpone_reason_text || selectedDelivery.cancel_reason_text}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setShowDetailModal(false)}
                className="w-full py-2.5 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-colors"
              >
                Хаах
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between bg-white rounded-xl shadow-sm border border-gray-200 px-4 py-3">
          <div className="text-sm text-gray-600">
            Хуудас {pagination.page}/{pagination.totalPages} (Нийт: {pagination.total})
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
              disabled={pagination.page <= 1}
              className="px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              ← Өмнөх
            </button>
            
            {/* Page numbers */}
            <div className="flex gap-1">
              {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                let pageNum;
                if (pagination.totalPages <= 5) {
                  pageNum = i + 1;
                } else if (pagination.page <= 3) {
                  pageNum = i + 1;
                } else if (pagination.page >= pagination.totalPages - 2) {
                  pageNum = pagination.totalPages - 4 + i;
                } else {
                  pageNum = pagination.page - 2 + i;
                }
                return (
                  <button
                    key={pageNum}
                    onClick={() => setPagination(prev => ({ ...prev, page: pageNum }))}
                    className={`w-8 h-8 text-sm font-medium rounded-lg ${
                      pagination.page === pageNum
                        ? 'bg-indigo-600 text-white'
                        : 'border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
              disabled={pagination.page >= pagination.totalPages}
              className="px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Дараах →
            </button>
          </div>
        </div>
      )}
       {/* EDIT DELIVERY MODAL */}
      {showEditModal && editDelivery && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4 rounded-t-xl flex justify-between items-center">
              <h3 className="text-lg font-bold">Хүргэлт засах</h3>
              <button onClick={() => setShowEditModal(false)} className="p-1 hover:bg-white/20 rounded-lg">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-4 space-y-3">
              {/* Үндсэн мэдээлэл */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Хүлээн авагч</label>
                  <input type="text" value={editDelivery.customer_name}
                    onChange={(e) => setEditDelivery({...editDelivery, customer_name: e.target.value})}
                    className="w-full border rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Утас</label>
                  <input type="text" value={editDelivery.customer_phone}
                    onChange={(e) => setEditDelivery({...editDelivery, customer_phone: e.target.value})}
                    className="w-full border rounded-lg px-3 py-2 text-sm" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Хаяг</label>
                <input type="text" value={editDelivery.address}
                  onChange={(e) => setEditDelivery({...editDelivery, address: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2 text-sm" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                   <label className="block text-sm font-medium text-gray-700 mb-1">Барааны дүн (₮)</label>
                  <input type="number" value={editDelivery.cod_amount}
                    readOnly
                    className="w-full border rounded-lg px-3 py-2 text-sm bg-gray-100 cursor-not-allowed" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Төлбөрийн төлөв</label>
                   <select value={editDelivery.payment_status}
                    onChange={(e) => {
                      const newStatus = e.target.value;
                      const newCod = newStatus === 'prepaid' ? 0 : calculateCodFromItems(editItems, newStatus);
                      setEditDelivery({...editDelivery, payment_status: newStatus, cod_amount: newCod});
                    }}
                    className="w-full border rounded-lg px-3 py-2 text-sm">
                    <option value="unpaid">Төлөгдөөгүй</option>
                    <option value="prepaid">Урьдчилж төлсөн</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Тэмдэглэл</label>
                <textarea value={editDelivery.notes}
                  onChange={(e) => setEditDelivery({...editDelivery, notes: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2 text-sm" rows={2} />
              </div>

              {/* БАРАА ХЭСЭГ */}
              <div className="border-t pt-3">
                <h4 className="text-sm font-bold text-gray-800 mb-2">📦 Бараанууд</h4>
                
                {/* Бараа хайх */}
                <div className="relative mb-3">
                  <input
                    type="text"
                    placeholder="Бараа хайх (нэр, SKU)..."
                    value={editProductSearch}
                    onChange={(e) => setEditProductSearch(e.target.value)}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                  />
                  {editProductSearch.length > 1 && (
                    <div className="absolute z-10 w-full bg-white border rounded-lg shadow-lg mt-1 max-h-48 overflow-y-auto">
                      {availableProducts
                        .filter(p => 
                          p.product_name?.toLowerCase().includes(editProductSearch.toLowerCase()) ||
                          p.sku?.toLowerCase().includes(editProductSearch.toLowerCase())
                        )
                        .slice(0, 10)
                        .map(product => (
                          <button
                            key={product.id}
                            onClick={() => handleEditAddProduct(product)}
                            className="w-full text-left px-3 py-2 hover:bg-blue-50 text-sm border-b last:border-0"
                          >
                            <span className="font-medium">{product.product_name}</span>
                            <span className="text-gray-500 ml-2">({product.sku})</span>
                            <span className="text-green-600 ml-2">Үлдэгдэл: {product.stock_quantity}</span>
                          </button>
                        ))
                      }
                      {availableProducts.filter(p => 
                        p.product_name?.toLowerCase().includes(editProductSearch.toLowerCase()) ||
                        p.sku?.toLowerCase().includes(editProductSearch.toLowerCase())
                      ).length === 0 && (
                        <p className="px-3 py-2 text-sm text-gray-500">Бараа олдсонгүй</p>
                      )}
                    </div>
                  )}
                </div>

                {/* Нэмэгдсэн бараанууд */}
                {editItems.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-3">Бараа нэмэгдээгүй</p>
                ) : (
                  <div className="space-y-2">
                    {editItems.map((item, index) => (
                      <div key={index} className="flex items-center gap-2 bg-gray-50 rounded-lg p-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{item.product_name}</p>
                          {item.variant_name && <p className="text-xs text-gray-500">{item.variant_name}</p>}
                          <p className="text-xs text-gray-400">{item.sku}</p>
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleEditItemQuantity(index, item.quantity - 1)}
                            className="w-7 h-7 flex items-center justify-center bg-white border rounded text-gray-600 hover:bg-gray-100"
                          >-</button>
                          <input
                            type="number"
                            value={item.quantity}
                            onChange={(e) => handleEditItemQuantity(index, parseInt(e.target.value) || 1)}
                            className="w-14 text-center border rounded py-1 text-sm"
                            min="1"
                          />
                          <button
                            onClick={() => handleEditItemQuantity(index, item.quantity + 1)}
                            className="w-7 h-7 flex items-center justify-center bg-white border rounded text-gray-600 hover:bg-gray-100"
                          >+</button>
                        </div>
                        <span className="text-sm font-medium w-24 text-right">
                          {(item.unit_price * item.quantity).toLocaleString()}₮
                        </span>
                        <button
                          onClick={() => handleEditRemoveItem(index)}
                          className="p-1 text-red-500 hover:bg-red-50 rounded"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                    <div className="flex justify-between items-center pt-2 border-t">
                      <span className="text-sm font-medium text-gray-600">Нийт:</span>
                      <span className="text-lg font-bold text-blue-600">
                        {editItems.reduce((sum, item) => sum + (item.unit_price * item.quantity), 0).toLocaleString()}₮
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="p-4 border-t flex gap-3">
              <button onClick={() => setShowEditModal(false)}
                className="flex-1 py-2.5 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300">
                Болих
              </button>
              <button onClick={handleEditSave}
                className="flex-1 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700">
                Хадгалах
              </button>
            </div>
          </div>
        </div>
      )}
    </ShopLayout>
  );
};
export default Deliveries;